import styled from '@emotion/styled';
import React, { FC, useEffect, useState } from 'react';
import { CartDiscount, useCart } from '~Components/CartProvider';
import PayButton from '~Components/PayButton';
import PayWithButton from '~Components/PayWithButton';
import SplitPayment from '~Components/SplitPayment';
import BeeperSelect from '~Components/BeeperSelect';
import Loader from '~ComponentsImproved/Loader';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import {
  useAdyen,
  useButton,
  useDeviceInfo,
  useDispatch,
  useLogger,
  useMetadata,
  useSelector,
  useStaffCan,
} from '~Hooks';
import Discounts from '~Scenes/POS/Components/Cart/Components/Discounts';
import { GreenButton, WhiteButton } from '~Styles';
import useI18n from '~Translations';
import Journal from '~/Utils/journal';
import { DISCOUNT_TYPE, ORDER_ORIGIN, SERVICE_TYPE } from '../../../../../__queries__';
import { useStorage } from '~Components/Storage';
import { CompleteBongQuery_venue_company_offers as Offer } from '~Scenes/POS/Queries/__queries__';
import * as actions from '~actions';
import useBongQuery from '~Scenes/POS/Queries/query';

const QsrButtonsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 8px;
  position: relative;
  margin-top: 8px;
`;

const QsrButtonsOuterWrapper = styled.div`
  margin: 16px 12px;
`;

const PayWithWrapper = styled.div`
  position: relative;
`;

/** Matches the margin QsrButtonsWrapper puts under the Discounts row. */
const StoredCardWrapper = styled.div`
  margin-top: 8px;
`;

const StoredCardPayButton = styled(GreenButton)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 100%;
  height: 88px;
`;

const StoredCardPayLabel = styled.span`
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
`;

const StoredCardSubLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  opacity: 0.85;
`;

const RemoveCardButton = styled(WhiteButton)`
  width: 100%;
  height: 36px;
  margin-top: 8px;
  font-size: 13px;
`;

const RemovingLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

interface Props {
  venueId: string;
  orderId: string;
  orderWasBonged: boolean;
  onComplete: () => void;
  setPaymentMethod: (id: string, poiid?: string) => void;
  isQsr?: boolean;
}

const QsrPayButtons: FC<Props> = ({
  venueId,
  orderId,
  orderWasBonged,
  onComplete,
  setPaymentMethod,
  isQsr,
}) => {
  const logger = useLogger('payment');
  const deviceInfo = useDeviceInfo();
  const demoPopup = useModal();
  const staffCan = useStaffCan();
  const cart = useCart();
  const adyen = useAdyen();
  const dispatch = useDispatch();
  const staffId = useSelector((state) => state.staff?.id);
  const { i18n } = useI18n();
  const [advancedPaymentDiscount, setAdvancedPaymentDiscount] = useState<any>(null);
  const splitPaymentModal = useModal();
  const [storage] = useStorage();
  const bongQuery = useBongQuery(venueId);
  const metadata = useMetadata();

  const paymentMethods = useSelector((state) => state.POSPaymentMethods);
  const filteredPaymentMethods = paymentMethods.filter(
    (pm) => pm.venues.some((v) => v === venueId) || pm.venues.length === 0,
  );

  const [showBeeperSelector, setShowBeeperSelector] = useState<boolean>(false);
  const [pendingPayment, setPendingPayment] = useState<{
    discount: CartDiscount | null;
    method: string;
    poiid?: string;
  } | null>(null);

  const demoMode = useSelector((state) => state.demoMode);
  if (demoMode) {
    venueId = window.DEMO_MODE_VENUE_ID;
  }

  const primaryPaymentMethod = filteredPaymentMethods.find((pm) => pm.isPrimary);
  const hasAdyen = filteredPaymentMethods.some((method) => method.id === 'ADYEN');
  const hasZettle = filteredPaymentMethods.some((method) => method.id === 'IZETTLE');

  // Check if there is a default payment method available
  const hasDefaultPaymentMethod =
    deviceInfo?.specificPaymentMethod || primaryPaymentMethod?.id || hasAdyen || hasZettle;

  const storedCardTerminalId = storage.adyenTerminals[0]?.POIID;

  const [isRemovingCard, setIsRemovingCard] = useState(false);

  const storedCardPayButton = useButton({
    onClick: () => handlePay(cart.appliedDiscount, 'ADYEN', storedCardTerminalId),
    disabled: cart.items.length === 0 || !staffCan.MAKE_PAYMENT,
    disabledToast: !staffCan.MAKE_PAYMENT ? i18n('Staff.NoPermission.MakePayment') : undefined,
  });

  const removeCardButton = useButton({
    onClick: async () => {
      setIsRemovingCard(true);

      try {
        // The terminal is still holding the card data and reports Busy to
        // everything else, so release it before clearing our own state.
        if (storedCardTerminalId) {
          const res = await adyen.cancelCardAcquisition(storedCardTerminalId);

          if (!res.success) {
            logger.warn(`Could not release the card acquisition: ${res.message}`, {
              terminalId: storedCardTerminalId,
            });
          }
        }

        cart.setCardIdentity(null);
      } finally {
        setIsRemovingCard(false);
      }
    },
    disabled: isRemovingCard,
  });

  const splitPayButton = useButton({
    onClick: () => {
      setAdvancedPaymentDiscount(cart.appliedDiscount);
      splitPaymentModal.show();
    },
    disabled: cart.items.length === 0 || !staffCan.MAKE_PAYMENT,
    disabledToast: !staffCan.MAKE_PAYMENT ? i18n('Staff.NoPermission.MakePayment') : undefined,
  });

  function pay(_appliedDiscount: CartDiscount | null, method: string, poiid?: string) {
    let discountType: DISCOUNT_TYPE | null = null;
    let discountRate: number | null = null;

    if (demoMode) {
      demoPopup.toggle();
      return;
    }

    Journal.write('Payment Button Pressed', method);

    setPaymentMethod(method, poiid);

    if (_appliedDiscount) {
      cart.setAppliedDiscount(_appliedDiscount);
      discountType = _appliedDiscount.type;
      discountRate = _appliedDiscount.value;
    }

    const messages = cart.packagedItems.filter((item) => item.isMessage);
    const comment = messages.map((message) => message.title).join(', ') || '';

    dispatch(
      // @ts-ignore
      actions.initiateOrder({
        desiredTime: cart.desiredTime || undefined,
        id: orderId,
        orderNumber: cart.easyId || '',
        serviceType: storage.enableServiceTypeDelivery ? SERVICE_TYPE.DELIVERY : cart.serviceType,
        discountType,
        discountRate,
        defaultOrderStatus: bongQuery.data?.company?.defaultOrderStatus,
        method,
        origin: ORDER_ORIGIN.POS,
        zoneId: storage.zoneId,
        bonged: orderWasBonged,
        staffId,
        comment,
        userId: cart.regularUserId || cart.loyaltyUserId,
        // Staff ticked "connect card" in the loyalty modal. Travels with the
        // order to createPosOrder so the loyalty service can store the card the
        // member pays with. Only meaningful with a loyalty member on the cart.
        connectCardToUser: cart.loyaltyUserId ? cart.connectCardToMember : undefined,
      }),
    );

    logger.info(`QSR: Initiating payment. Method: ${method}.`, {
      id: orderId,
      method,
      serviceType: cart.serviceType,
      zoneId: storage.zoneId,
      poiid,
    });
  }

  // When a beeper is selected and easyId updates, proceed with the pending payment
  useEffect(() => {
    if (pendingPayment && cart.easyId) {
      pay(pendingPayment.discount, pendingPayment.method, pendingPayment.poiid);
      setPendingPayment(null);
    }
  }, [cart.easyId]);

  // Intercept payment to show beeper selector if required and no beeper has been set
  function handlePay(discount: CartDiscount | null, method: string, poiid?: string) {
    const hasBeeper = cart.easyId && cart.easyId.includes('•');

    if (storage.requireBeeperNumber && metadata.data.beeperList.length > 0 && !hasBeeper) {
      setPendingPayment({ discount, method, poiid });
      setShowBeeperSelector(true);
      return;
    }

    pay(discount, method, poiid);
  }

  return (
    <>
      <QsrButtonsOuterWrapper>
        {(staffCan.MANUAL_DISCOUNT || staffCan.PRESET_DISCOUNT) && (
          <Discounts
            fullWidth
            disabled={cart.items.length === 0}
            forSelectedItems={cart.selectedItems.ids.length > 0}
          />
        )}
        {cart.cardIdentity ? (
          /**
           * The shopper already presented their card to identify themselves, so
           * the order is committed to charging that card. Offering other
           * methods here would strand the acquisition on the terminal.
           */
          <StoredCardWrapper>
            <StoredCardPayButton {...storedCardPayButton} data-testid="pay-with-stored-card">
              <StoredCardPayLabel>{i18n('POS.Pay')}</StoredCardPayLabel>
              <StoredCardSubLabel>
                {cart.cardIdentity.memberName
                  ? i18n('POS.UseCardOf', cart.cardIdentity.memberName.split(' ')[0])
                  : i18n('POS.UseStoredCard')}
              </StoredCardSubLabel>
            </StoredCardPayButton>
            <RemoveCardButton {...removeCardButton}>
              {isRemovingCard ? (
                <RemovingLabel>
                  <Loader size="sm" color="#888" />
                  {i18n('Loyalty.Cancelling')}
                </RemovingLabel>
              ) : (
                i18n('POS.RemoveCard')
              )}
            </RemoveCardButton>
          </StoredCardWrapper>
        ) : (
          <QsrButtonsWrapper>
            <WhiteButton
              {...splitPayButton}
              style={{
                height: '56px',
                gridColumn: !hasDefaultPaymentMethod ? 'span 2' : 'span 1',
              }}>
              {i18n('POS.SplitPayment')}
            </WhiteButton>
            <PayWithWrapper
              style={{
                gridColumn: !hasDefaultPaymentMethod ? 'span 2' : 'span 1',
              }}>
              <PayWithButton
                isQSR
                disabled={cart.items.length === 0}
                style={{
                  height: hasDefaultPaymentMethod ? '56px' : '72px',
                }}
                whiteButton={Boolean(hasDefaultPaymentMethod)}
                fullButtonText
                onPay={(paymentMethodId, poiid) =>
                  handlePay(cart.appliedDiscount, paymentMethodId, poiid)
                }
              />
            </PayWithWrapper>
            <PayButton
              style={{ height: '72px', gridColumn: 'span 2' }}
              disabled={cart.items.length === 0}
              onPay={(paymentMethodId, poiid) =>
                handlePay(cart.appliedDiscount, paymentMethodId, poiid)
              }
              isQsr={isQsr}
            />
          </QsrButtonsWrapper>
        )}
      </QsrButtonsOuterWrapper>

      {showBeeperSelector && (
        <BeeperSelect
          beeperList={metadata.data.beeperList}
          onSelect={(beeperId) => {
            cart.setEasyId(beeperId);
            setShowBeeperSelector(false);
          }}
          onCancel={() => {
            setShowBeeperSelector(false);
            setPendingPayment(null);
          }}
        />
      )}

      <Modal
        isShowing={splitPaymentModal.isShowing}
        hide={splitPaymentModal.hide}
        backdropClose={false}
        zIndex={50}>
        <SplitPayment
          orderId={orderId}
          total={cart.total - cart.discount}
          onClose={splitPaymentModal.hide}
          appliedDiscount={advancedPaymentDiscount}
          standardPay={pay}
          onComplete={onComplete}
        />
      </Modal>
    </>
  );
};

export default QsrPayButtons;
